import React, { useState } from 'react';
import { Mail, Instagram, Send, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase, type ContactSubmissionData } from '../lib/supabase';
import { inputClassSm } from '../constants/classNames';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    course: '',
    message: '',
    privacy: false,
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const contactData: ContactSubmissionData = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        course: formData.course || null,
        message: formData.message,
        privacy_accepted: formData.privacy,
      };

      // Kein .select() nach dem Insert: RETURNING erfordert Leserechte,
      // die auf contact_submissions nur Admins haben (RLS).
      const { error } = await supabase
        .from('contact_submissions')
        .insert([contactData]);

      if (error) {
        if (import.meta.env.DEV) console.error('Fehler beim Speichern der Kontaktanfrage:', error);
        setSubmitError('Fehler beim Senden der Nachricht. Bitte versuche es erneut oder kontaktiere uns direkt per E-Mail.');
        return;
      }

      const { error: mailError } = await supabase.functions.invoke('send-contact-confirmation', {
        body: {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          course: formData.course,
          message: formData.message,
        },
      });

      if (mailError) console.error('E-Mail Fehler:', mailError);
      setIsSubmitted(true);

      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', subject: '', course: '', message: '', privacy: false });
      }, 5000);
    } catch (error) {
      if (import.meta.env.DEV) console.error('Unerwarteter Fehler:', error);
      setSubmitError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showCourseField = formData.subject === 'course-questions';

  const inputClass = inputClassSm;

  return (
    <section id="contact" className="bg-white py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="max-w-2xl mb-8 md:mb-12">
          <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[#970000] mb-3">
            Kontakt
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
            Wir helfen dir{' '}
            <span className="oblique font-extrabold text-[#970000]">persönlich weiter</span>
          </h2>
          <p className="mt-3 md:mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
            Kostenlose Beratung — wir helfen dir, den passenden Einstieg zu finden.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6 md:gap-8 items-start">

          {/* Left — contact info */}
          <div className="space-y-3">
            <div className="rounded-xl border border-gray-100 bg-white p-5 flex items-start gap-4 hover:border-[#970000]/30 transition-all duration-200">
              <div className="w-9 h-9 rounded-lg bg-[#970000]/[0.08] flex items-center justify-center text-[#970000] shrink-0">
                <Mail className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-gray-900 mb-0.5">E-Mail</p>
                <a href="mailto:info@arabicroots.de" className="text-[13px] text-gray-500 hover:text-[#970000] transition-colors">
                  info@arabicroots.de
                </a>
                <p className="text-[12px] text-gray-400 mt-1">Antwort in der Regel innerhalb von 24 Stunden.</p>
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-white p-5 flex items-start gap-4 hover:border-[#970000]/30 transition-all duration-200">
              <div className="w-9 h-9 rounded-lg bg-[#970000]/[0.08] flex items-center justify-center text-[#970000] shrink-0">
                <Instagram className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-gray-900 mb-0.5">Instagram</p>
                <a
                  href="https://instagram.com/arabic.roots"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[13px] text-gray-500 hover:text-[#970000] transition-colors"
                >
                  @arabic.roots
                </a>
                <p className="text-[12px] text-gray-400 mt-1">Schreib uns eine DM für schnelle Antworten.</p>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl bg-[#b30000] p-5 text-white">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/15" />
              <div className="relative">
                <p className="text-[13px] font-semibold text-white/60 mb-1">Persönliche Beratung</p>
                <p className="text-[13px] text-white leading-relaxed">
                  Noch unsicher welcher Kurs passt? Wir beraten dich gerne individuell und kostenlos.
                </p>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="rounded-xl border border-gray-100 bg-white p-4 sm:p-6 md:p-7">
            <h3 className="text-xl font-bold text-gray-900 mb-6 tracking-[-0.02em]">
              Nachricht senden
            </h3>

            {isSubmitted ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 rounded-full bg-green-50 border border-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h4 className="text-[15px] font-semibold text-gray-900 mb-2">Nachricht erfolgreich gesendet</h4>
                <p className="text-[13px] text-gray-500 leading-relaxed max-w-sm mx-auto">
                  Vielen Dank! Wir melden uns so schnell wie möglich bei dir.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {submitError && (
                  <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3">
                    <p className="text-sm text-red-600">{submitError}</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                      Name *
                    </label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange}
                      className={inputClass} placeholder="Dein Name" required disabled={isSubmitting} />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                      E-Mail *
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                      className={inputClass} placeholder="deine@email.de" required disabled={isSubmitting} />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                    Betreff *
                  </label>
                  <select id="subject" name="subject" value={formData.subject} onChange={handleChange}
                    className={inputClass} required disabled={isSubmitting}>
                    <option value="">Betreff auswählen</option>
                    <option value="general">Allgemeine Fragen</option>
                    <option value="course-questions">Fragen zum Kurs</option>
                    <option value="consultation">Beratung</option>
                    <option value="other">Sonstiges</option>
                  </select>
                </div>

                {showCourseField && (
                  <div>
                    <label htmlFor="course" className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                      Welcher Kurs? *
                    </label>
                    <select id="course" name="course" value={formData.course} onChange={handleChange}
                      className={inputClass} required={showCourseField} disabled={isSubmitting}>
                      <option value="">Kurs auswählen</option>
                      <option value="level1">Level 1 - Buchstabenkurs</option>
                      <option value="level2">Level 2 - Grundlagenkurs</option>
                      <option value="level3">Level 3 - Aufbaukurs</option>
                      <option value="level4">Level 4 - Fortgeschrittenenkurs</option>
                      <option value="level5">Level 5 - Expertenkurs</option>
                      <option value="level6">Level 6 - Meisterstufe</option>
                      <option value="custom">Individueller Kurs</option>
                      <option value="unsure">Ich bin mir nicht sicher</option>
                    </select>
                  </div>
                )}

                <div>
                  <label htmlFor="message" className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                    Nachricht *
                  </label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange}
                    rows={4} className={`${inputClass} resize-none`}
                    placeholder="Erzähl uns von deinen Zielen..." required disabled={isSubmitting} />
                </div>

                <div className="rounded-lg border border-gray-100 bg-gray-50/60 px-4 py-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" id="privacy" name="privacy" checked={formData.privacy}
                      onChange={handleChange}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#970000] focus:ring-[#970000]"
                      required disabled={isSubmitting} />
                    <span className="text-[12px] text-gray-500 leading-relaxed">
                      Ich stimme zu, dass meine Angaben zur Kontaktaufnahme gespeichert werden.
                      {' '}<Link to="/datenschutz" className="text-[#970000] hover:underline">Datenschutzerklärung</Link>.
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={!formData.privacy || isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-[14px] font-semibold text-white disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-filter"
                  style={(!formData.privacy || isSubmitting) ? undefined : { background: 'linear-gradient(135deg,rgba(255,255,255,.15),transparent 50%,rgba(0,0,0,.15)), #b30000' }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Nachricht senden
                    </>
                  )}
                </button>

                <p className="text-[11px] text-gray-400 text-center">* Pflichtfelder</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
